"use client";

import { useEffect, useState } from 'react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/Button';
import { sanitizeRedirect } from '@/lib/redirect';

type Provider = 'google' | 'apple' | 'discord';

const PROVIDER_NAME: Record<Provider, string> = { google: 'Google', apple: 'Apple', discord: 'Discord' };

interface SocialAuthButtonsProps {
  /**
   * Optional internal path (starts with `/`) to land on after OAuth completes.
   * Re-sanitized here; the server validates it again on the OAuth callback.
   */
  redirectTo?: string;
  /** Called when the server starts no redirect (rare): the caller refreshes or closes. */
  onSuccess?: () => void;
  /** Disable the buttons while the surrounding form is busy. */
  disabled?: boolean;
  /** Reports an OAuth start in flight, so the surrounding form can disable itself. */
  onPendingChange?: (pending: boolean) => void;
}

/**
 * Social sign-in. The same buttons create an account on first use:
 * the OAuth callback makes the profile when public signup is on (or the email
 * holds an invitation), so the register form offers them as well as login.
 */
export function SocialAuthButtons({ redirectTo, onSuccess, disabled = false, onPendingChange }: SocialAuthButtonsProps) {
  const [pending, setPending] = useState<Provider | null>(null);
  const [appleEnabled, setAppleEnabled] = useState(false);

  useEffect(() => {
    const controller = new AbortController();
    void fetch('/api/auth/oauth/providers', { signal: controller.signal, cache: 'no-store' })
      .then(response => response.ok ? response.json() : null)
      .then(providers => {
        if (!controller.signal.aborted) setAppleEnabled(providers?.apple === true);
      })
      .catch(() => { /* Keep Apple hidden if provider discovery is unavailable. */ });
    return () => controller.abort();
  }, []);

  const start = async (provider: Provider) => {
    setPending(provider); onPendingChange?.(true);
    try {
      // Keep OAuth initiation same-origin so one release image can run on beta or dev.
      const response = await fetch(`/api/auth/oauth/${provider}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ returnTo: sanitizeRedirect(redirectTo) ?? window.location.pathname + window.location.search }),
      });
      const result = await response.json();
      if (result.success) {
        if (result.redirectTo) window.location.href = result.redirectTo;
        else onSuccess?.();
      } else {
        toast.error(result.message || `${PROVIDER_NAME[provider]} sign-in failed. Please try again.`);
      }
    } catch (error) {
      console.error(`${PROVIDER_NAME[provider]} OAuth error:`, error);
      toast.error(`An unexpected error occurred with ${PROVIDER_NAME[provider]} sign-in.`);
    } finally {
      setPending(null); onPendingChange?.(false);
    }
  };

  const busy = disabled || pending !== null;
  return (
    <>
      <Button
        onClick={() => void start('google')}
        className="w-full bg-white text-gray-900 border border-gray-300 hover:bg-gray-50 flex items-center justify-center gap-3 font-medium"
        disabled={busy}
        type="button"
      >
        <svg className="w-5 h-5" viewBox="0 0 24 24" aria-hidden="true">
          <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
          <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
          <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
          <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
        </svg>
        {pending === 'google' ? 'Connecting to Google...' : 'Continue with Google'}
      </Button>

      {appleEnabled && <Button
        onClick={() => void start('apple')}
        className="w-full bg-black text-white border border-white hover:bg-gray-900 flex items-center justify-center gap-3 font-medium"
        disabled={busy}
        type="button"
      >
        <svg className="w-5 h-5" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
          <path d="M17.05 12.04c.03 3.17 2.78 4.22 2.81 4.23-.02.07-.44 1.51-1.45 3-.88 1.29-1.79 2.57-3.23 2.6-1.42.03-1.88-.84-3.5-.84-1.63 0-2.14.81-3.49.87-1.39.05-2.45-1.4-3.33-2.68-1.81-2.62-3.19-7.42-1.33-10.65a5.17 5.17 0 0 1 4.39-2.65c1.37-.03 2.66.93 3.5.93.83 0 2.4-1.15 4.04-.98.69.03 2.63.28 3.88 2.11-.1.06-2.32 1.35-2.29 4.06ZM14.4 4.13c.73-.89 1.22-2.13 1.08-3.36-1.05.04-2.33.7-3.08 1.59-.67.77-1.25 2.02-1.09 3.22 1.17.09 2.36-.59 3.09-1.45Z" />
        </svg>
        {pending === 'apple' ? 'Connecting to Apple...' : 'Continue with Apple'}
      </Button>}

      <Button
        onClick={() => void start('discord')}
        className="w-full bg-[#5865F2] text-white border border-[#5865F2] hover:bg-[#4752C4] flex items-center justify-center gap-3 font-medium"
        disabled={busy}
        type="button"
      >
        <svg className="w-5 h-5" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
          <path d="M20.317 4.37a19.791 19.791 0 0 0-4.885-1.515.074.074 0 0 0-.079.037c-.21.375-.444.864-.608 1.25a18.27 18.27 0 0 0-5.487 0 12.64 12.64 0 0 0-.617-1.25.077.077 0 0 0-.079-.037A19.736 19.736 0 0 0 3.677 4.37a.07.07 0 0 0-.032.027C.533 9.046-.32 13.58.099 18.057a.082.082 0 0 0 .031.057 19.9 19.9 0 0 0 5.993 3.03.078.078 0 0 0 .084-.028c.462-.63.874-1.295 1.226-1.994a.076.076 0 0 0-.041-.106 13.107 13.107 0 0 1-1.872-.892.077.077 0 0 1-.008-.128 10.2 10.2 0 0 0 .372-.292.074.074 0 0 1 .077-.01c3.928 1.793 8.18 1.793 12.062 0a.074.074 0 0 1 .078.01c.12.098.246.198.373.292a.077.077 0 0 1-.006.127 12.299 12.299 0 0 1-1.873.892.077.077 0 0 0-.041.107c.36.698.772 1.362 1.225 1.993a.076.076 0 0 0 .084.028 19.839 19.839 0 0 0 6.002-3.03.077.077 0 0 0 .032-.054c.5-5.177-.838-9.674-3.549-13.66a.061.061 0 0 0-.031-.03zM8.02 15.33c-1.183 0-2.157-1.085-2.157-2.419 0-1.333.956-2.419 2.157-2.419 1.21 0 2.176 1.096 2.157 2.42 0 1.333-.956 2.418-2.157 2.418zm7.975 0c-1.183 0-2.157-1.085-2.157-2.419 0-1.333.955-2.419 2.157-2.419 1.21 0 2.176 1.096 2.157 2.42 0 1.333-.946 2.418-2.157 2.418z"/>
        </svg>
        {pending === 'discord' ? 'Connecting to Discord...' : 'Continue with Discord'}
      </Button>
    </>
  );
}
